<template>
  <transition name="modal-overlay">
    <div
			v-if="isVisible"
			class="modal-v2"
			:class="getClasses"
		>
      <div class="modal-v2__overlay" @click="overlayClicked()" ></div>
      <transition name="modal-card">
        <div v-if="cardVisible" class="modal-v2__card-wrapper">
          <focus-trap
            v-model:active="cardVisible"
            :initial-focus="() => $refs.modalCard"
            :escape-deactivates="closeOnClickOutside"
            @deactivate='isVisible = false'
          >
            <div
              class="modal-v2__card"
              :class="[cardSize, { 'modal-heartbeat' : overlayAnimation }]"
              tabindex="-1"
              ref="modalCard"
            >
              <button
                v-if="!hideCloseButton"
                class="modal-v2__trigger"
                @click="[close(), $emit('defaultCloseButtonTriggered')]"
              >
                <ion-icon class="icon" name="close"/>
              </button>

              <div v-if="customContent" class="custom-content">
                <slot name="custom-content"></slot>
              </div>

              <template v-else>
                <div class="modal-v2__header" v-if="!hideDefaultHeader">
                  <slot name="header">
                    <ion-icon
                      v-if="icon"
                      class="w-10 h-10 mb-4 text-primary"
                      :name="icon"
                    />

                    <h2>{{ title }}</h2>
                  </slot>
                </div>

                <div :class="['modal-v2__body', getBodyClasses]">
                  <slot name="body">
                    <p> {{ text }} </p>
                  </slot>
                </div>

                <div class="modal-v2__footer" v-if="!hideDefaultFooter">
                  <slot name="footer">
                    <button
                      class="modal-v2__button modal-v2__button--primary"
                      @click="close"
                    >
                      {{ closeButtonText }}
                    </button>
                  </slot>
                </div>
              </template>

            </div>
          </focus-trap>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import {mapState, mapActions} from "vuex";
import { FocusTrap } from "focus-trap-vue";

export default {
  name: "modal-v2",

  components: {FocusTrap},

	props: {
		/**
		 * The modal card size
		 * allowed values: small (default, doesn't need to be declared), medium and large
		 */
		size: {
			type: String,
			default: ''
		},

    /**
		 * The title displayed in the modal header
		 */
		title: {
			type: String,
			default: ""
		},

    /**
		 * The icon displayed in the modal header
		 */
		icon: {
			type: String,
			default: ""
		},

		/**
		 * The text displayed in the modal body
		 */
		text: {
			type: String,
			default: ''
		},

		/**
		 * The text displayed in the action button
		 */
		closeButtonText: {
			type: String,
			default: 'Fechar'
		},

    /**
		 * Whether the footer should be rendered
		 */
		hideDefaultFooter: {
			type: Boolean,
			default: false
		},

    /**
		 * Whether the header should be rendered
		 */
    hideDefaultHeader: {
      type: Boolean,
			default: false
    },

    /**
     * Whether the modal should have custom content
     * overring the default modal structure
     */
    customContent: {
      type: Boolean,
			default: false
    },

    /**
		 * Whether the modal should be closed
		 * when overlay is clicked
		 */
		closeOnClickOutside: {
			type: Boolean,
			default: false
		},

    /**
		 * Whether the close button should be hidden
		 */
		hideCloseButton: {
			type: Boolean,
			default: false
		},

    /**
		 * Text alignment of the modal body
		 */
		textAlign: {
			type: String,
			default: 'center',
			validator: (value) => ['center', 'left'].includes(value)
		},

		/**
		 * Wheter the modal body should have auto height
		 */
		autoBodyHeight: Boolean,

		/**
		 * Wheter the overflow of the modal body should be visible
		 */
		unlockBodyOverflow: Boolean,
	},

	data() {
		return {
			uniqueId: this._uid,
			isVisible: false,
			cardVisible: false,
			overlayAnimation: false
		};
	},

	computed: {
		...mapState("modalV2", ["modalId"]),

		// Get the modalId from vuex store
		getModalId() {
			return this.modalId;
		},

		cardSize() {
      if(this.size) {
        return `modal-v2__card--${this.size}`;
      }
		},

		/**
		 * Set the modal classes based on props
		 * @returns {String} Modal classes
		 */
		getClasses() {
			return {
				'modal-v2--unlocked': this.unlockBodyOverflow,
			};
		},

    /**
		 * Set the body classes based on props
		 * @returns {String} Modal body classes
		 */
		getBodyClasses() {
			return {
				[`modal-v2__body--${this.textAlign}-text`]: this.textAlign,
				'modal-v2__body--height-auto': this.autoBodyHeight,
			};
		},
	},

	watch: {

		/*
		 * Watchs store state, if modalId != uniqueId
		 * it means the modalId in the store has changed
		 * so we need to close our modal
		 */
		getModalId(storeId) {
			if (storeId !== this.uniqueId) {
				this.cardVisible = false;
				setTimeout(() => this.isVisible = false, 100);
			}
		},

		/**
		 * Checks if the modal is visible
		 * If its not visible emit modalClosed event to parent
		 */
		isVisible(visible) {
			if (visible) {
				this.setModalV2Visible(this.uniqueId);

				return;
			}

			this.$emit('modalClosed');
		},

    /**
		 * Checks if the modal card is visible
		 * If it's true, emit a modalOpened event to the parent
		 */
		cardVisible(visible) {
			if (visible) {
				this.$emit('modalOpened');
			}
		}
	},

	methods: {
		...mapActions("modalV2", ["setModalV2Visible"]),

    /**
		 * Displays the modal
		 * @returns {void}
		 */
		open() {
			this.isVisible = true;
			setTimeout(() => this.cardVisible = true, 100);
		},

    /**
		 * Hides the modal
		 * @returns {void}
		 */
		close() {
			this.setModalV2Visible(null);
		},

		/**
		 * Executes animation when overlay is clicked
		 * @returns {void}
		 */
		overlayClicked() {
      if (this.closeOnClickOutside) {
				this.close();

				return;
			}

			this.overlayAnimation = true;
			setTimeout(() => this.overlayAnimation = false, 160);
		}
	}
}
</script>
